/**
 * A small self-contained cell library: just enough of the TON cell model to
 * build wallet messages for the emulator and to take a bag of cells apart in
 * the cell inspector. Bits are kept as a string of '0'/'1' — every cell is at
 * most 1023 bits, so this is plenty fast and easy to slice.
 *
 * No dependency on tonweb / @ton/core, and no Buffer: works in the browser
 * bundle and in a bare node script alike.
 */

/* eslint no-bitwise: "off", no-use-before-define: "off", max-classes-per-file: "off", no-underscore-dangle: "off" */

const BOC_MAGIC = 0xb5ee9c72;

const HEX_RE = /^[0-9a-fA-F]+$/;

// ---- Byte helpers -----------------------------------------------------------

export const bytesToHex = bytes => Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');

export const hexToBytes = function (hex) {
    const clean = hex.replace(/^0x/i, '');
    const out = new Uint8Array(clean.length / 2);
    for (let i = 0; i < out.length; i += 1) {
        out[i] = parseInt(clean.substr(i * 2, 2), 16);
    }
    return out;
};

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export const bytesToBase64 = function (bytes, { urlSafe = false } = {}) {
    let out = '';
    for (let i = 0; i < bytes.length; i += 3) {
        const a = bytes[i];
        const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
        const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
        const triple = (a << 16) | (b << 8) | c;
        out += B64[(triple >> 18) & 63] + B64[(triple >> 12) & 63];
        out += i + 1 < bytes.length ? B64[(triple >> 6) & 63] : '=';
        out += i + 2 < bytes.length ? B64[triple & 63] : '=';
    }
    return urlSafe ? out.replace(/\+/g, '-').replace(/\//g, '_') : out;
};

export const base64ToBytes = function (base64) {
    const clean = base64.replace(/-/g, '+').replace(/_/g, '/').replace(/[\s=]+/g, '');
    if (!/^[A-Za-z0-9+/]*$/.test(clean)) {
        throw new Error('Invalid base64');
    }
    const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
    let buffer = 0;
    let bits = 0;
    let pos = 0;
    for (let i = 0; i < clean.length; i += 1) {
        buffer = (buffer << 6) | B64.indexOf(clean[i]);
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            out[pos] = (buffer >> bits) & 0xff;
            pos += 1;
        }
    }
    return out;
};

export const bytesToBits = function (bytes) {
    let bits = '';
    for (let i = 0; i < bytes.length; i += 1) {
        bits += bytes[i].toString(2).padStart(8, '0');
    }
    return bits;
};

// Bits → bytes, the tail padded with zeros:
export const bitsToBytes = function (bits) {
    const out = new Uint8Array(Math.ceil(bits.length / 8));
    for (let i = 0; i < out.length; i += 1) {
        out[i] = parseInt(bits.substr(i * 8, 8).padEnd(8, '0'), 2);
    }
    return out;
};

// The hex form fift prints: a trailing underscore marks a bit string that
// isn't byte aligned (the last nibble is padded with 1 then zeros).
export const bitsToFiftHex = function (bits) {
    if (bits.length % 4 === 0) {
        let out = '';
        for (let i = 0; i < bits.length; i += 4) {
            out += parseInt(bits.substr(i, 4), 2).toString(16);
        }
        return out.toUpperCase();
    }
    let padded = bits + '1';
    while (padded.length % 4 !== 0) padded += '0';
    return `${bitsToFiftHex(padded)}_`;
};

// ---- Cell ------------------------------------------------------------------

export class Cell {
    constructor({ bits = '', refs = [], exotic = false } = {}) {
        this.bits = bits;
        this.refs = refs;
        this.exotic = exotic;
    }

    /**
     * Exotic cell type, read from the first data byte; null for ordinary cells.
     * 1 = pruned branch, 2 = library, 3 = merkle proof, 4 = merkle update.
     */
    get exoticType() {
        if (!this.exotic || this.bits.length < 8) return null;
        return parseInt(this.bits.substr(0, 8), 2);
    }

    // Number of levels between this cell and the farthest leaf:
    get depth() {
        if (this.refs.length === 0) return 0;
        return 1 + Math.max(...this.refs.map(ref => ref.depth));
    }

    beginParse() {
        return new Slice(this);
    }

    toBoc(options) {
        return serializeBoc([this], options);
    }

    toBocBase64(options) {
        return bytesToBase64(this.toBoc(options));
    }

    // Structural identity, enough to de-duplicate cells inside one bag:
    get key() {
        if (this._key === undefined) {
            this._key = `${this.exotic ? 'x' : 'o'}${this.bits}|${this.refs.map(ref => ref.key).join(',')}`;
        }
        return this._key;
    }
}

// ---- Builder ---------------------------------------------------------------

export class Builder {
    constructor() {
        this.bits = '';
        this.refs = [];
    }

    storeBit(bit) {
        this.bits += bit ? '1' : '0';
        return this;
    }

    storeBits(bits) {
        this.bits += bits;
        return this;
    }

    storeUint(value, bitLength) {
        const big = BigInt(value);
        if (big < 0n || (bitLength < 64 && big >= (1n << BigInt(bitLength)))) {
            throw new Error(`Value ${value} does not fit into ${bitLength} bits`);
        }
        this.bits += big.toString(2).padStart(bitLength, '0');
        return this;
    }

    storeInt(value, bitLength) {
        const big = BigInt(value);
        const twos = big < 0n ? (1n << BigInt(bitLength)) + big : big;
        return this.storeUint(twos, bitLength);
    }

    storeBytes(bytes) {
        this.bits += bytesToBits(bytes);
        return this;
    }

    // VarUInteger 16: 4-bit byte count, then the value:
    storeCoins(value) {
        const big = BigInt(value);
        if (big === 0n) {
            return this.storeUint(0, 4);
        }
        const hex = big.toString(16);
        const bytes = Math.ceil(hex.length / 2);
        this.storeUint(bytes, 4);
        return this.storeUint(big, bytes * 8);
    }

    /**
     * @param {Object|null} address  { wc, hashPart: bytes } or null for addr_none
     */
    storeAddress(address) {
        if (!address) {
            return this.storeUint(0, 2);
        }
        this.storeUint(2, 2); // addr_std$10
        this.storeBit(0); // anycast: nothing
        this.storeInt(address.wc, 8);
        return this.storeBytes(address.hashPart);
    }

    storeRef(cell) {
        if (this.refs.length >= 4) {
            throw new Error('A cell holds at most 4 references');
        }
        this.refs.push(cell);
        return this;
    }

    storeMaybeRef(cell) {
        if (!cell) return this.storeBit(0);
        return this.storeBit(1).storeRef(cell);
    }

    // The tail of a snake string: whatever doesn't fit goes to a chained ref.
    storeStringTail(text) {
        const bytes = new TextEncoder().encode(text);
        const room = Math.floor((1023 - this.bits.length) / 8);
        this.storeBytes(bytes.subarray(0, room));
        if (bytes.length > room) {
            const tail = new Builder().storeStringTail(new TextDecoder().decode(bytes.subarray(room)));
            this.storeRef(tail.endCell());
        }
        return this;
    }

    endCell() {
        if (this.bits.length > 1023) {
            throw new Error(`Cell overflow: ${this.bits.length} bits`);
        }
        return new Cell({ bits: this.bits, refs: this.refs.slice() });
    }
}

export const beginCell = () => new Builder();

// ---- Slice -----------------------------------------------------------------

export class Slice {
    constructor(cell) {
        this.bits = cell.bits;
        this.refs = cell.refs.slice();
        this.offset = 0;
        this.refOffset = 0;
    }

    get remainingBits() {
        return this.bits.length - this.offset;
    }

    get remainingRefs() {
        return this.refs.length - this.refOffset;
    }

    preloadBits(count) {
        if (count > this.remainingBits) {
            throw new Error('Slice underflow');
        }
        return this.bits.substr(this.offset, count);
    }

    loadBits(count) {
        const bits = this.preloadBits(count);
        this.offset += count;
        return bits;
    }

    loadBit() {
        return this.loadBits(1) === '1';
    }

    loadUintBig(count) {
        if (count === 0) return 0n;
        return BigInt(`0b${this.loadBits(count)}`);
    }

    loadUint(count) {
        return Number(this.loadUintBig(count));
    }

    loadIntBig(count) {
        const raw = this.loadUintBig(count);
        return raw >= (1n << BigInt(count - 1)) ? raw - (1n << BigInt(count)) : raw;
    }

    loadInt(count) {
        return Number(this.loadIntBig(count));
    }

    loadCoins() {
        const bytes = this.loadUint(4);
        return this.loadUintBig(bytes * 8);
    }

    loadBytes(count) {
        return bitsToBytes(this.loadBits(count * 8));
    }

    /**
     * @return {Object|null} { wc, hashPart } for addr_std, null for addr_none
     */
    loadAddress() {
        const type = this.loadUint(2);
        if (type === 0) return null;
        if (type !== 2) {
            throw new Error('Unsupported address type');
        }
        if (this.loadBit()) {
            throw new Error('Anycast addresses are not supported');
        }
        const wc = this.loadInt(8);
        const hashPart = this.loadBytes(32);
        return { wc, hashPart };
    }

    loadRef() {
        if (this.refOffset >= this.refs.length) {
            throw new Error('No more references');
        }
        const ref = this.refs[this.refOffset];
        this.refOffset += 1;
        return ref;
    }

    loadMaybeRef() {
        return this.loadBit() ? this.loadRef() : null;
    }

    // Reads a snake string to the end of the cell chain:
    loadStringTail() {
        const chunks = [];
        let slice = this; // eslint-disable-line consistent-this
        for (;;) {
            const byteCount = Math.floor(slice.remainingBits / 8);
            chunks.push(slice.loadBytes(byteCount));
            if (slice.remainingRefs === 0) break;
            slice = slice.loadRef().beginParse();
        }
        const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const merged = new Uint8Array(total);
        let pos = 0;
        chunks.forEach((chunk) => {
            merged.set(chunk, pos);
            pos += chunk.length;
        });
        return new TextDecoder().decode(merged);
    }
}

// ---- Bag of cells: parse ---------------------------------------------------

const readUintBE = function (bytes, offset, size) {
    let value = 0;
    for (let i = 0; i < size; i += 1) {
        value = value * 256 + bytes[offset + i];
    }
    return value;
};

/**
 * Takes a serialized bag of cells apart.
 *
 * @param  {Uint8Array} bytes
 * @return {Object} { roots: Cell[], cells: Cell[], hasCrc, hasIndex }
 */
export const parseBoc = function deserializeBagOfCells(bytes) {
    if (bytes.length < 10 || readUintBE(bytes, 0, 4) !== BOC_MAGIC) {
        throw new Error('Not a bag of cells: wrong magic');
    }

    const flags = bytes[4];
    const hasIndex = Boolean(flags & 0x80);
    const hasCrc = Boolean(flags & 0x40);
    const refSize = flags & 7;
    const offsetSize = bytes[5];

    if (refSize < 1 || refSize > 4 || offsetSize < 1 || offsetSize > 8) {
        throw new Error('Malformed bag of cells header');
    }

    let cursor = 6;
    const cellCount = readUintBE(bytes, cursor, refSize);
    cursor += refSize;
    const rootCount = readUintBE(bytes, cursor, refSize);
    cursor += refSize;
    const absentCount = readUintBE(bytes, cursor, refSize);
    cursor += refSize;
    const totalCellsSize = readUintBE(bytes, cursor, offsetSize);
    cursor += offsetSize;

    if (absentCount !== 0) {
        throw new Error('Bags with absent cells are not supported');
    }
    if (rootCount < 1 || rootCount > cellCount) {
        throw new Error('Malformed bag of cells: root count');
    }

    const rootIndexes = [];
    for (let i = 0; i < rootCount; i += 1) {
        rootIndexes.push(readUintBE(bytes, cursor, refSize));
        cursor += refSize;
    }

    if (hasIndex) {
        cursor += cellCount * offsetSize;
    }

    const dataStart = cursor;
    if (bytes.length < dataStart + totalCellsSize + (hasCrc ? 4 : 0)) {
        throw new Error('Bag of cells is truncated');
    }

    if (hasCrc) {
        const expected = readUintLE(bytes, dataStart + totalCellsSize, 4);
        const actual = crc32c(bytes.subarray(0, dataStart + totalCellsSize));
        if (expected !== actual) {
            throw new Error('Bag of cells checksum mismatch');
        }
    }

    // First pass: raw records with ref indexes.
    const records = [];
    for (let i = 0; i < cellCount; i += 1) {
        const d1 = bytes[cursor];
        const d2 = bytes[cursor + 1];
        cursor += 2;

        const refCount = d1 & 7;
        const exotic = Boolean(d1 & 8);
        const hasHashes = Boolean(d1 & 16);
        const levelMask = d1 >> 5;

        if (refCount > 4) {
            throw new Error(`Cell #${i}: too many references`);
        }

        if (hasHashes) {
            const hashCount = popcount(levelMask) + 1;
            cursor += hashCount * (32 + 2);
        }

        const dataBytes = Math.ceil(d2 / 2);
        const data = bytes.subarray(cursor, cursor + dataBytes);
        cursor += dataBytes;

        let bits = bytesToBits(data);
        if (d2 % 2 === 1) {
            // Augmented: the last 1 bit is padding, drop it and the zeros after it:
            const last = bits.lastIndexOf('1');
            bits = last === -1 ? '' : bits.substr(0, last);
        }

        const refs = [];
        for (let r = 0; r < refCount; r += 1) {
            refs.push(readUintBE(bytes, cursor, refSize));
            cursor += refSize;
        }

        records.push({ bits, refs, exotic, levelMask });
    }

    if (cursor !== dataStart + totalCellsSize) {
        throw new Error('Bag of cells: cell data length mismatch');
    }

    // Second pass, from the end, so every referenced cell already exists:
    const cells = new Array(cellCount);
    for (let i = cellCount - 1; i >= 0; i -= 1) {
        const record = records[i];
        const refs = record.refs.map((index) => {
            if (index <= i || index >= cellCount) {
                throw new Error(`Cell #${i}: reference to cell #${index} is out of order`);
            }
            return cells[index];
        });
        cells[i] = new Cell({ bits: record.bits, refs, exotic: record.exotic });
    }

    return {
        roots: rootIndexes.map(index => cells[index]),
        cells,
        hasCrc,
        hasIndex,
    };
};

/**
 * Accepts base64, url-safe base64 or hex and returns the roots.
 * @param  {String} input
 * @return {Object} { roots, cells, encoding }
 */
export const parseBocString = function (input) {
    const text = input.trim();
    if (!text) {
        throw new Error('Empty input');
    }
    const hexLike = text.replace(/^0x/i, '').replace(/\s+/g, '');
    if (HEX_RE.test(hexLike) && hexLike.length % 2 === 0 && hexLike.toLowerCase().startsWith('b5ee9c72')) {
        return { ...parseBoc(hexToBytes(hexLike)), encoding: 'hex' };
    }
    return { ...parseBoc(base64ToBytes(text.replace(/\s+/g, ''))), encoding: 'base64' };
};

// ---- Bag of cells: serialize ---------------------------------------------

/**
 * Orders the cells so that every cell comes before the cells it references
 * (a reversed post-order walk of the DAG), de-duplicating equal cells.
 */
const topologicalOrder = function (roots) {
    const seen = new Map();
    const order = [];

    const visit = (cell) => {
        if (seen.has(cell.key)) return;
        seen.set(cell.key, true);
        cell.refs.forEach(visit);
        order.push(cell);
    };

    roots.forEach(visit);
    order.reverse();

    const indexes = new Map();
    order.forEach((cell, index) => indexes.set(cell.key, index));

    return { order, indexes };
};

const bytesNeeded = function (value) {
    let size = 1;
    while (value >= 256 ** size) size += 1;
    return size;
};

const writeUintBE = function (out, offset, value, size) {
    let remaining = value;
    for (let i = size - 1; i >= 0; i -= 1) {
        out[offset + i] = remaining & 0xff; // eslint-disable-line no-param-reassign
        remaining = Math.floor(remaining / 256);
    }
};

export const serializeBoc = function serializeBagOfCells(roots, { crc = false } = {}) {
    const { order, indexes } = topologicalOrder(roots);
    const refSize = bytesNeeded(order.length);

    const cellChunks = order.map((cell) => {
        const dataBytes = bitsToBytes(cell.bits);
        const aligned = cell.bits.length % 8 === 0;
        if (!aligned) {
            // Augment: a 1 right after the data, zeros to the byte end.
            const bitInByte = cell.bits.length % 8;
            dataBytes[dataBytes.length - 1] |= 1 << (7 - bitInByte);
        }
        const chunk = new Uint8Array(2 + dataBytes.length + cell.refs.length * refSize);
        chunk[0] = cell.refs.length + (cell.exotic ? 8 : 0);
        chunk[1] = Math.ceil(cell.bits.length / 8) + Math.floor(cell.bits.length / 8);
        chunk.set(dataBytes, 2);
        cell.refs.forEach((ref, i) => {
            writeUintBE(chunk, 2 + dataBytes.length + i * refSize, indexes.get(ref.key), refSize);
        });
        return chunk;
    });

    const totalCellsSize = cellChunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const offsetSize = bytesNeeded(totalCellsSize);

    const headerSize = 4 + 1 + 1 + refSize * 3 + offsetSize + roots.length * refSize;
    const out = new Uint8Array(headerSize + totalCellsSize + (crc ? 4 : 0));

    writeUintBE(out, 0, BOC_MAGIC, 4);
    out[4] = (crc ? 0x40 : 0) | refSize;
    out[5] = offsetSize;
    let cursor = 6;
    writeUintBE(out, cursor, order.length, refSize);
    cursor += refSize;
    writeUintBE(out, cursor, roots.length, refSize);
    cursor += refSize;
    writeUintBE(out, cursor, 0, refSize);
    cursor += refSize;
    writeUintBE(out, cursor, totalCellsSize, offsetSize);
    cursor += offsetSize;
    roots.forEach((root) => {
        writeUintBE(out, cursor, indexes.get(root.key), refSize);
        cursor += refSize;
    });
    cellChunks.forEach((chunk) => {
        out.set(chunk, cursor);
        cursor += chunk.length;
    });

    if (crc) {
        const sum = crc32c(out.subarray(0, cursor));
        out[cursor] = sum & 0xff;
        out[cursor + 1] = (sum >>> 8) & 0xff;
        out[cursor + 2] = (sum >>> 16) & 0xff;
        out[cursor + 3] = (sum >>> 24) & 0xff;
    }

    return out;
};

// ---- Hashes ----------------------------------------------------------------

const subtle = () => (globalThis.crypto && globalThis.crypto.subtle) || null;

export const sha256 = async function (bytes) {
    const api = subtle();
    if (!api) {
        throw new Error('WebCrypto is not available');
    }
    return new Uint8Array(await api.digest('SHA-256', bytes));
};

/**
 * Representation hash of an ordinary cell tree (level 0). Cells with an
 * exotic cell anywhere below them get null: their hashes depend on the
 * pruned levels and are out of scope here.
 *
 * Fills `cell.hash` (hex) on every cell of the tree and returns the root's.
 */
export const computeCellHashes = async function (cell) {
    if (cell.hash !== undefined) return cell.hash;

    const childHashes = [];
    for (let i = 0; i < cell.refs.length; i += 1) {
        childHashes.push(await computeCellHashes(cell.refs[i])); // eslint-disable-line no-await-in-loop
    }

    if (cell.exotic || childHashes.some(hash => hash === null)) {
        cell.hash = null; // eslint-disable-line no-param-reassign
        return null;
    }

    const data = bitsToBytes(cell.bits);
    if (cell.bits.length % 8 !== 0) {
        data[data.length - 1] |= 1 << (7 - (cell.bits.length % 8));
    }

    const repr = new Uint8Array(2 + data.length + cell.refs.length * (2 + 32));
    repr[0] = cell.refs.length;
    repr[1] = Math.ceil(cell.bits.length / 8) + Math.floor(cell.bits.length / 8);
    repr.set(data, 2);
    let cursor = 2 + data.length;
    cell.refs.forEach((ref) => {
        const depth = ref.depth;
        repr[cursor] = depth >> 8;
        repr[cursor + 1] = depth & 0xff;
        cursor += 2;
    });
    childHashes.forEach((hash) => {
        repr.set(hexToBytes(hash), cursor);
        cursor += 32;
    });

    cell.hash = bytesToHex(await sha256(repr)); // eslint-disable-line no-param-reassign
    return cell.hash;
};

// ---- CRC32C (Castagnoli), the checksum bags of cells carry ----------------

let crcTable;

const getCrcTable = function () {
    if (!crcTable) {
        crcTable = new Int32Array(256);
        for (let n = 0; n < 256; n += 1) {
            let c = n;
            for (let k = 0; k < 8; k += 1) {
                c = c & 1 ? (c >>> 1) ^ 0x82f63b78 : c >>> 1;
            }
            crcTable[n] = c;
        }
    }
    return crcTable;
};

export const crc32c = function (bytes) {
    const table = getCrcTable();
    let crc = -1;
    for (let i = 0; i < bytes.length; i += 1) {
        crc = table[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ -1) >>> 0;
};

const readUintLE = function (bytes, offset, size) {
    let value = 0;
    for (let i = size - 1; i >= 0; i -= 1) {
        value = value * 256 + bytes[offset + i];
    }
    return value;
};

const popcount = function (value) {
    let count = 0;
    let v = value;
    while (v) {
        count += v & 1;
        v >>= 1;
    }
    return count;
};
